'use client';

import React, { useState, useRef, useEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

export interface PopoverProps {
  /** Trigger element. Popover anchors to its bounding box. */
  trigger: React.ReactElement;
  /** Popover body content. Can be interactive (buttons, links, forms). */
  children: React.ReactNode;
  /** Preferred side. Falls back to opposite side if it would clip. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Alignment along the side. */
  align?: 'start' | 'center' | 'end';
  /** Classes applied to the popover panel. */
  className?: string;
  /** Controlled open state. Omit for uncontrolled. */
  open?: boolean;
  /** Called when open state changes (controlled or uncontrolled). */
  onOpenChange?: (open: boolean) => void;
  /** Close when Escape is pressed. Default true. */
  closeOnEscape?: boolean;
  /** Close when clicking outside. Default true. */
  closeOnOutsideClick?: boolean;
  /**
   * ARIA role applied to the panel. Default 'dialog' for interactive popovers
   * with form controls or actions. Use 'region' for informational popovers
   * (e.g. FieldHint `?` icons, AboutThisScreen info popover) so screen readers
   * don't falsely announce "dialog" and avoid unnecessary focus-trap behaviour.
   */
  role?: 'dialog' | 'region' | 'tooltip' | 'menu';
  /** Accessible label when role='region'. Defaults to "More information". */
  ariaLabel?: string;
  /**
   * z-index for the portalled panel. Default 55. Raise this above a higher
   * stacking context when the popover is opened from inside one — e.g. the
   * mobile nav drawer renders at z-70, so its submenu popovers must pass a
   * value > 70 or they render *behind* the opaque drawer (invisible on mobile).
   */
  zIndex?: number;
}

/**
 * Popover — click-to-open floating panel. Good for `?` icon hints with
 * interactive content (links, buttons). For pure hover-text use Tooltip.
 *
 * The trigger must accept `onClick` and `ref` — typically a Button.
 */
export function Popover({
  trigger,
  children,
  side = 'bottom',
  align = 'center',
  className,
  open: controlledOpen,
  onOpenChange,
  closeOnEscape = true,
  closeOnOutsideClick = true,
  role = 'dialog',
  ariaLabel,
  zIndex = 55,
}: PopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const popoverId = useId();

  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const panelW = panelRef.current?.offsetWidth ?? 280;
    const panelH = panelRef.current?.offsetHeight ?? 120;

    let top = 0;
    let left = 0;

    switch (side) {
      case 'bottom':
        top = rect.bottom + margin;
        left =
          align === 'start'
            ? rect.left
            : align === 'end'
              ? rect.right - panelW
              : rect.left + rect.width / 2 - panelW / 2;
        if (top + panelH > window.innerHeight) {
          top = rect.top - panelH - margin; // flip
        }
        break;
      case 'top':
        top = rect.top - panelH - margin;
        left =
          align === 'start'
            ? rect.left
            : align === 'end'
              ? rect.right - panelW
              : rect.left + rect.width / 2 - panelW / 2;
        if (top < 0) top = rect.bottom + margin;
        break;
      case 'right':
        left = rect.right + margin;
        top =
          align === 'start'
            ? rect.top
            : align === 'end'
              ? rect.bottom - panelH
              : rect.top + rect.height / 2 - panelH / 2;
        break;
      case 'left':
        left = rect.left - panelW - margin;
        top =
          align === 'start'
            ? rect.top
            : align === 'end'
              ? rect.bottom - panelH
              : rect.top + rect.height / 2 - panelH / 2;
        break;
    }

    // Clamp to viewport
    const clampedPanelW = Math.min(panelW, window.innerWidth - margin * 2);
    const clampedPanelH = Math.min(panelH, window.innerHeight - margin * 2);
    left = Math.max(margin, Math.min(left, window.innerWidth - clampedPanelW - margin));
    top = Math.max(margin, Math.min(top, window.innerHeight - clampedPanelH - margin));
    setPosition({ top, left });
  }, [side, align]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    computePosition();
    // Recompute on resize / scroll
    const handler = () => computePosition();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [isOpen, computePosition]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        setOpen(false);
      }
    };
    const handleClick = (e: MouseEvent) => {
      if (!closeOnOutsideClick) return;
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [isOpen, closeOnEscape, closeOnOutsideClick, setOpen]);

  const triggerElement = React.cloneElement(trigger, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      const triggerAny = trigger as React.ReactElement & { ref?: React.Ref<HTMLElement> };
      const origRef = triggerAny.ref;
      if (typeof origRef === 'function') origRef(node);
      else if (origRef && typeof origRef === 'object') {
        (origRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    onClick: (e: React.MouseEvent) => {
      setOpen(!isOpen);
      (trigger.props as { onClick?: (e: React.MouseEvent) => void }).onClick?.(e);
    },
    'aria-expanded': isOpen,
    'aria-haspopup': role === 'dialog' ? 'dialog' : role === 'menu' ? 'menu' : 'true',
    'aria-controls': isOpen ? popoverId : undefined,
  } as Record<string, unknown>);

  const panel =
    isOpen && typeof window !== 'undefined'
      ? createPortal(
          <div
            id={popoverId}
            ref={panelRef}
            role={role}
            aria-label={role !== 'dialog' ? (ariaLabel ?? 'More information') : undefined}
            style={{
              position: 'fixed',
              top: position?.top ?? -9999,
              left: position?.left ?? -9999,
              zIndex,
              visibility: position ? 'visible' : 'hidden',
            }}
            className={cn(
              'min-w-0 max-w-[calc(100vw-1rem)] overflow-auto break-words rounded-xl border border-slate-200 bg-white p-4 shadow-lg sm:min-w-[240px] sm:max-w-sm',
              'animate-in fade-in-0 zoom-in-95 duration-150',
              className,
            )}
          >
            {children}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {triggerElement}
      {panel}
    </>
  );
}
